/** /org/admin → Políticas (T5): solución + puesto (+ cliente) → herramienta, rol, momento y aprobador. */
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Plus, Upload, Pencil, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { InternalTable, EmptyState, LoaderBlock } from '@/components/internal/shared';
import { EnumBadge } from '@/components/ui/enum-badge';
import { toastError } from '@/lib/toast-error';
import { useOrgCatalogMutations, useOrgClientsAll, useOrgPolicies, useOrgTools } from '@/hooks/useOrg';
import { ClientPicker } from '@/components/org/ClientPicker';
import { CsvImportDialog } from '@/components/org/CsvImportDialog';
import { OrgClientLink, OrgSolutionLink } from '@/components/org/org-ui';
import { solutionName } from '@/components/org/org-format';
import { ORG_POLICY_MOMENTS, ORG_SOLUTIONS, type OrgPolicyMoment } from '../../../../shared/types/org';
import { buildClientNameIndex, resolveClientId, solutionByText } from '../../../../shared/org-rules';
import { foldKey } from '../../../../shared/text-normalize';
import type { RouterOutputs } from '@/lib/api-types';

type Policy = RouterOutputs['org']['policies']['list'][number];
/** Forma explícita (el input del server usa z.preprocess y su tipo inferido es `unknown` por campo). */
interface PolicyInput {
  solution_slug: string;
  role_title: string | null;
  client_id: string | null;
  tool_slug: string;
  tool_role: string | null;
  moment: OrgPolicyMoment;
  approver_email: string | null;
}
const NONE = '__none__';
const ALL = '__all__';

const POLICY_CSV_FIELDS = {
  solution_slug: ['solucion', 'solution'],
  role_title: ['puesto', 'rol', 'role', 'role title'],
  client_id: ['cliente', 'client'],
  tool_slug: ['herramienta', 'tool'],
  tool_role: ['rol en la herramienta', 'tool role', 'permiso'],
  moment: ['momento', 'cuando', 'when'],
  approver_email: ['aprobador', 'approver', 'aprueba'],
} as const;
type PolicyCsvKey = keyof typeof POLICY_CSV_FIELDS;

const MOMENT_ALIASES: Record<string, OrgPolicyMoment> = { day1: 'day1', 'dia 1': 'day1', 'dia1': 'day1', ingreso: 'day1', on_assignment: 'on_assignment', 'al asignar': 'on_assignment', asignacion: 'on_assignment', cliente: 'on_assignment' };

export function OrgPoliciesTab() {
  const { t } = useTranslation();
  const [solution, setSolution] = useState<string>(ALL);
  const q = useOrgPolicies(solution === ALL ? undefined : { solutionSlug: solution });
  const tools = useOrgTools();
  const clients = useOrgClientsAll();
  const m = useOrgCatalogMutations();
  const [editing, setEditing] = useState<Policy | 'new' | null>(null);
  const [removing, setRemoving] = useState<Policy | null>(null);
  const [importing, setImporting] = useState(false);
  const clientIndex = useMemo(() => buildClientNameIndex((clients.data ?? []).map((c) => ({ id: c.client_id, displayName: c.display_name }))), [clients.data]);
  const clientName = (id: string) => clients.data?.find((c) => c.client_id === id)?.display_name ?? id;
  const csvRow = useCallback((r: Partial<Record<PolicyCsvKey, string>>): { value: PolicyInput } | { error: string } => {
    const sol = r.solution_slug ? solutionByText(r.solution_slug, { partial: true }) : null;
    if (!sol) return { error: t('org.csv.err_solution', 'Solución desconocida: {{value}}', { value: r.solution_slug ?? '' }) };
    const tool = r.tool_slug ? (tools.data ?? []).find((x) => x.slug === r.tool_slug!.trim().toLowerCase() || foldKey(x.name) === foldKey(r.tool_slug!)) : null;
    if (!tool) return { error: t('org.csv.err_tool', 'Herramienta que no está en el catálogo: {{value}}', { value: r.tool_slug ?? '' }) };
    const moment = r.moment ? MOMENT_ALIASES[foldKey(r.moment)] : undefined;
    if (!moment) return { error: t('org.csv.err_moment', 'Momento desconocido (día 1 / al asignar): {{value}}', { value: r.moment ?? '' }) };
    let client_id: string | null = null;
    if (r.client_id) {
      const direct = (clients.data ?? []).find((c) => c.client_id === r.client_id!.trim().toLowerCase());
      const res = direct ? { clientId: direct.client_id } : resolveClientId(r.client_id, clientIndex);
      if (!res) return { error: t('org.csv.err_client', 'Cliente no encontrado: {{value}}', { value: r.client_id }) };
      client_id = res.clientId;
    }
    if (moment === 'day1' && client_id) return { error: t('org.csv.err_day1_client', 'Una política de día 1 no lleva cliente') };
    return { value: { solution_slug: sol.slug, role_title: r.role_title || null, client_id, tool_slug: tool.slug, tool_role: r.tool_role || null, moment, approver_email: r.approver_email || null } };
  }, [t, tools.data, clients.data, clientIndex]);
  if (q.isLoading || tools.isLoading) return <LoaderBlock />;
  if (q.isError || tools.isError) return <div className="space-y-2"><EmptyState title={t('org.policies.error', 'No se pudieron leer las políticas.')} /><Button variant="outline" size="sm" onClick={() => { q.refetch(); tools.refetch(); }}>{t('common.retry', 'Reintentar')}</Button></div>;
  const rows = q.data ?? [];
  const toolName = (slug: string) => tools.data?.find((x) => x.slug === slug)?.name ?? slug;
  const cols = [
    { key: 'solution', label: t('org.policies.col_solution', 'Solución'), render: (p: Policy) => <OrgSolutionLink slug={p.solution_slug} /> },
    { key: 'role', label: t('org.policies.col_role', 'Puesto'), render: (p: Policy) => p.role_title ?? <span className="text-muted-foreground">{t('org.policies.any_role', 'Cualquiera')}</span> },
    { key: 'client', label: t('org.policies.col_client', 'Cliente'), render: (p: Policy) => (p.client_id ? <OrgClientLink clientId={p.client_id} name={clientName(p.client_id)} /> : <span className="text-muted-foreground">{t('org.policies.any_client', 'Todos')}</span>) },
    { key: 'tool', label: t('org.policies.col_tool', 'Herramienta'), render: (p: Policy) => <span className="font-medium">{toolName(p.tool_slug)}{p.tool_role ? <span className="ml-1 text-xs text-muted-foreground">({p.tool_role})</span> : null}</span> },
    { key: 'moment', label: t('org.policies.col_moment', 'Cuándo'), render: (p: Policy) => <EnumBadge value={p.moment} tone={p.moment === 'day1' ? 'accent' : 'info'} label={t(`org.policies.moment_${p.moment}`, p.moment)} /> },
    { key: 'approver', label: t('org.policies.col_approver', 'Aprueba'), render: (p: Policy) => <span className="text-muted-foreground">{p.approver_email ?? '—'}</span> },
    { key: 'actions', label: '', render: (p: Policy) => (
      <span className="flex gap-1">
        <Button size="sm" variant="ghost" onClick={() => setEditing(p)} aria-label={t('common.edit', 'Editar')}><Pencil className="h-4 w-4" /></Button>
        <Button size="sm" variant="ghost" onClick={() => setRemoving(p)} aria-label={t('common.delete', 'Borrar')}><Trash2 className="h-4 w-4" /></Button>
      </span>
    ) },
  ];
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Label htmlFor="pol-filter" className="text-sm">{t('org.policies.filter_solution', 'Solución')}</Label>
          <Select value={solution} onValueChange={setSolution}>
            <SelectTrigger id="pol-filter" className="w-56"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>{t('org.policies.all_solutions', 'Todas')}</SelectItem>
              {ORG_SOLUTIONS.map((s) => <SelectItem key={s.slug} value={s.slug}>{s.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => setImporting(true)} disabled={!tools.data?.length}><Upload className="mr-1 h-4 w-4" />{t('org.csv.import', 'Importar CSV')}</Button>
          <Button size="sm" onClick={() => setEditing('new')} disabled={!tools.data?.length}><Plus className="mr-1 h-4 w-4" />{t('org.policies.new', 'Nueva política')}</Button>
        </div>
      </div>
      <p className="text-sm text-muted-foreground">{t('org.policies.help', 'Qué herramienta y rol le corresponde a cada puesto de una solución: el día 1 (al ingresar) o al confirmarse una asignación con un cliente. Con esto el Org Service calcula qué le falta y qué le sobra a cada persona.')}</p>
      {!tools.data?.length && <EmptyState title={t('org.policies.no_tools', 'Primero cargá el catálogo de herramientas.')} />}
      {rows.length ? <InternalTable columns={cols} rows={rows} rowKey={(p) => String(p.id)} /> : tools.data?.length ? <EmptyState title={t('org.policies.empty', 'Todavía no hay políticas.')} /> : null}
      {editing && <PolicyDialog policy={editing === 'new' ? null : editing} tools={tools.data ?? []} onClose={() => setEditing(null)} />}
      <AlertDialog open={!!removing} onOpenChange={(o) => !o && setRemoving(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('org.policies.remove_title', '¿Borrar esta política?')}</AlertDialogTitle>
            <AlertDialogDescription>{removing ? `${solutionName(removing.solution_slug)} · ${removing.role_title ?? t('org.policies.any_role', 'Cualquiera')} → ${toolName(removing.tool_slug)}` : ''}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel', 'Cancelar')}</AlertDialogCancel>
            <AlertDialogAction disabled={m.busy} onClick={(e) => { e.preventDefault(); if (!removing) return; m.policyRemove.mutate({ id: removing.id }, { onSuccess: () => { toast.success(t('org.policies.removed', 'Política borrada')); setRemoving(null); }, onError: (err) => toastError(err, 'org.policies.error') }); }}>{t('common.delete', 'Borrar')}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <CsvImportDialog<PolicyCsvKey, PolicyInput>
        open={importing}
        onOpenChange={setImporting}
        title={t('org.policies.import_title', 'Importar políticas desde CSV')}
        description={t('org.policies.import_body', 'Una política por fila. Columnas: solución, puesto (vacío = cualquiera), cliente (vacío = todos), herramienta (nombre o slug del catálogo), rol en la herramienta, momento (día 1 / al asignar), aprobador.')}
        fields={POLICY_CSV_FIELDS}
        required={['solution_slug', 'tool_slug', 'moment']}
        templateHeader="solución;puesto;cliente;herramienta;rol en la herramienta;momento;aprobador"
        toRow={csvRow}
        onImport={async (rowsIn) => { try { return await m.policyImport.mutateAsync({ rows: rowsIn }); } catch (err) { toastError(err, 'org.policies.error'); throw err; } }}
        pending={m.busy}
      />
    </div>
  );
}

function PolicyDialog({ policy, tools, onClose }: { policy: Policy | null; tools: RouterOutputs['org']['tools']['list']; onClose: () => void }) {
  const { t } = useTranslation();
  const m = useOrgCatalogMutations();
  const clients = useOrgClientsAll();
  const [v, setV] = useState<PolicyInput>(policy ? { solution_slug: policy.solution_slug, role_title: policy.role_title, client_id: policy.client_id, tool_slug: policy.tool_slug, tool_role: policy.tool_role, moment: policy.moment, approver_email: policy.approver_email } : { solution_slug: ORG_SOLUTIONS[0].slug, role_title: null, client_id: null, tool_slug: tools[0]?.slug ?? '', tool_role: null, moment: 'day1', approver_email: null });
  const set = <K extends keyof PolicyInput>(k: K, val: PolicyInput[K]) => setV((p) => ({ ...p, [k]: val }));
  const tool = tools.find((x) => x.slug === v.tool_slug);
  const save = () => {
    const onError = (err: unknown) => toastError(err, 'org.policies.error');
    const onSuccess = () => { toast.success(t('org.policies.saved', 'Política guardada')); onClose(); };
    if (policy) m.policyUpdate.mutate({ ...v, id: policy.id }, { onSuccess, onError });
    else m.policyCreate.mutate(v, { onSuccess, onError });
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{policy ? t('org.policies.edit_title', 'Editar política') : t('org.policies.new', 'Nueva política')}</DialogTitle>
          <DialogDescription>{t('org.policies.dialog_body', 'Puesto vacío = aplica a toda la solución. Cliente vacío = a todos los clientes (solo tiene sentido "al asignar").')}</DialogDescription>
        </DialogHeader>
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-1">
            <Label htmlFor="pol-solution">{t('org.policies.col_solution', 'Solución')}</Label>
            <Select value={v.solution_slug} onValueChange={(val) => set('solution_slug', val)}>
              <SelectTrigger id="pol-solution"><SelectValue /></SelectTrigger>
              <SelectContent>{ORG_SOLUTIONS.map((s) => <SelectItem key={s.slug} value={s.slug}>{s.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label htmlFor="pol-role">{t('org.policies.col_role', 'Puesto')}</Label>
            <Input id="pol-role" value={v.role_title ?? ''} onChange={(e) => set('role_title', e.target.value || null)} placeholder={t('org.policies.any_role', 'Cualquiera')} />
          </div>
          <div className="space-y-1">
            <Label htmlFor="pol-tool">{t('org.policies.col_tool', 'Herramienta')}</Label>
            <Select value={v.tool_slug} onValueChange={(val) => setV((p) => ({ ...p, tool_slug: val, tool_role: null }))}>
              <SelectTrigger id="pol-tool"><SelectValue /></SelectTrigger>
              <SelectContent>{tools.map((x) => <SelectItem key={x.slug} value={x.slug}>{x.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label htmlFor="pol-tool-role">{t('org.policies.col_tool_role', 'Rol en la herramienta')}</Label>
            {tool?.roles.length ? (
              <Select value={v.tool_role ?? NONE} onValueChange={(val) => set('tool_role', val === NONE ? null : val)}>
                <SelectTrigger id="pol-tool-role"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value={NONE}>—</SelectItem>{tool.roles.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
              </Select>
            ) : (
              <Input id="pol-tool-role" value={v.tool_role ?? ''} onChange={(e) => set('tool_role', e.target.value || null)} />
            )}
          </div>
          <div className="space-y-1">
            <Label htmlFor="pol-moment">{t('org.policies.col_moment', 'Cuándo')}</Label>
            <Select value={v.moment} onValueChange={(val) => setV((p) => ({ ...p, moment: val as OrgPolicyMoment, client_id: val === 'day1' ? null : p.client_id }))}>
              <SelectTrigger id="pol-moment"><SelectValue /></SelectTrigger>
              <SelectContent>{ORG_POLICY_MOMENTS.map((mo) => <SelectItem key={mo} value={mo}>{t(`org.policies.moment_${mo}`, mo)}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label htmlFor="pol-client">{t('org.policies.col_client', 'Cliente')}</Label>
            {v.moment === 'on_assignment' ? (
              <div className="flex items-center gap-2">
                <div className="flex-1"><ClientPicker id="pol-client" clients={clients.data ?? []} value={v.client_id} onChange={(c) => set('client_id', c)} loading={clients.isLoading} error={clients.isError} onRetry={() => clients.refetch()} /></div>
                {v.client_id && <Button type="button" variant="ghost" size="sm" onClick={() => set('client_id', null)}>{t('org.policies.any_client', 'Todos')}</Button>}
              </div>
            ) : <p className="text-xs text-muted-foreground">{t('org.policies.day1_no_client', 'Día 1 no lleva cliente.')}</p>}
          </div>
          <div className="space-y-1 sm:col-span-2">
            <Label htmlFor="pol-approver">{t('org.policies.col_approver', 'Aprueba')}</Label>
            <Input id="pol-approver" type="email" value={v.approver_email ?? ''} onChange={(e) => set('approver_email', e.target.value || null)} placeholder="nombre@winclap.com" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>{t('common.cancel', 'Cancelar')}</Button>
          <Button onClick={save} disabled={m.busy || !v.tool_slug}>{t('common.save', 'Guardar')}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
